import React from 'react';
import PropTypes from 'prop-types';
import graphqlPropTypes from '@prisjakt/graphql-proptypes';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string-for-all';
import ShareIcon from 'mdi-react/ShareIcon';
import { AppContext } from '@prisjakt/ui-app-context';
import PriceHistoryTable from './components/price-history-table';
import PriceTrend from '../../components/graph/components/price-trend';
import PriceHistoryCurve from '../../components/graph/components/price-history-curve';
import MedianPrice from '../../components/graph/components/median-price';
import MaxPrice from '../../components/graph/components/max-price';
import PricePrediction from '../../components/graph/components/price-prediction';
import ImportantDates from '../../components/graph/components/important-dates';
import StorePriceLegend from '../../components/graph/components/store-price-legend';
import FetchContext from '../../components/fetch-context';
import AdContainer from '../../components/ads';
import IntervalSelector, {
  subtractFromDate,
  toDateString,
} from './components/interval-selector';
import GraphSelector from './components/graph-selector';
import Modal from '../../components/modal';
import ModalActions from '../../components/modal/actions';
import { ProductPriceHistoryPageQuery } from '../../types';
import priceHistoryQuery from './price-history-query.graphql';

import * as S from './styles';

const IMPORTANT_DATES = 'important-dates';
const PRICE_TREND = 'price-trend';
const PRICE_PREDICTION = 'price-prediction';
const PRICE_HISTORY = 'price-history';
const MEDIAN_PRICE = 'median-price';
const MAX_PRICE = 'max-price';

const adsViewUpdateAction = () => ({
  type: 'ADS_VIEW_UPDATE',
  view: 'priceHistory:mounted',
});

const queryStringGraph = {
  dates: IMPORTANT_DATES,
  forecast: PRICE_PREDICTION,
  price: PRICE_HISTORY,
  trend: PRICE_TREND,
  median: MEDIAN_PRICE,
  max: MAX_PRICE,
};

const filterProductNodes = (prices, minDate) => {
  if (!minDate) {
    return prices;
  }

  const startDate = new Date(minDate);

  return prices.filter(({ date }) => new Date(date) >= startDate);
};

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

// Calculate number of days between two dates.
const daysBetweenDates = (date1, date2) =>
  Math.round(
    Math.abs(
      (new Date(date1).getTime() - new Date(date2).getTime()) /
        MILLISECONDS_PER_DAY
    )
  );

// We only have one data point per month for mean prices. This function filter
// out dates before the start date and add a new point on the start date with
// correct data.
const interpolateMeanPrice = (data, startDate) => {
  const before = data.filter(({ date }) => date < startDate);

  if (before.length === 0) {
    return data;
  }

  const after = data.filter(({ date }) => date >= startDate);
  const lastFromBefore = before[before.length - 1];

  const originalDayDiff = daysBetweenDates(lastFromBefore.date, after[0].date);
  const startDateDayDiff = daysBetweenDates(startDate, after[0].date);

  const meanPricePerDay =
    (lastFromBefore.meanPrice - after[0].meanPrice) / originalDayDiff;
  const lowestPricePerDay =
    (lastFromBefore.lowestPrice - after[0].lowestPrice) / originalDayDiff;
  const highestPricePerDay =
    (lastFromBefore.highestPrice - after[0].highestPrice) / originalDayDiff;

  return [
    {
      date: startDate,
      meanPrice: after[0].meanPrice + meanPricePerDay * startDateDayDiff,
      lowestPrice: after[0].lowestPrice + lowestPricePerDay * startDateDayDiff,
      highestPrice:
        after[0].highestPrice + highestPricePerDay * startDateDayDiff,
    },
    ...after,
  ];
};

class ProductPriceHistory extends React.PureComponent {
  constructor(props) {
    super(props);

    this.shareGraphInputRef = React.createRef();

    let graphs;
    const qs = queryString.parse(this.props.location.search, {
      arrayFormat: 'comma',
    });
    if (qs.graphs) {
      // The query string library will return different data types depending
      // on the number of items in the query string. Multiple items will
      // result in an array; a single item will result in a string.
      if (typeof qs.graphs === 'string') {
        qs.graphs = [qs.graphs];
      }
      graphs = qs.graphs.map(g => queryStringGraph[g] || false).filter(x => x);
    }

    let startDate;
    if (qs.period) {
      startDate = toDateString(subtractFromDate(qs.period, new Date()));
    }

    this.state = {
      selectedPrices: {},
      expandedPrices: {},
      priceHistory: {},
      selectedStartDate: {
        name: undefined,
        min: startDate || undefined,
        period: qs.period || undefined,
      },
      selectedGraphs: graphs || [PRICE_HISTORY, PRICE_PREDICTION],
    };
  }

  componentDidMount() {
    // Explicitly tell ads to update visibility/load (needed when lazy loading pages)
    this.props.adsViewUpdateAction();
  }

  // Get a URL with the current graph settings encoded as query string parameters.
  getShareUrl() {
    const { market } = this.context;
    const qs = queryString.parse(this.props.location.search, {
      arrayFormat: 'comma',
    });

    qs.graphs = this.state.selectedGraphs.map(g =>
      Object.keys(queryStringGraph).find(key => queryStringGraph[key] === g)
    );

    qs.period =
      this.state.selectedStartDate.period === 'all'
        ? undefined
        : this.state.selectedStartDate.period;

    return `${market.domain}${
      this.props.location.pathname
    }?${queryString.stringify(qs, {
      arrayFormat: 'comma',
    })}`;
  }

  fetchPriceHistory(id) {
    if (this.state.priceHistory[id]) {
      // We already have price history, or are already working on fetching it.
      return;
    }

    const { fetchContext } = this.props;

    // Mark the price history as being fetched
    this.setState(prevState => ({
      priceHistory: {
        ...prevState.priceHistory,
        [id]: { fetching: true },
      },
    }));

    fetchContext.query(priceHistoryQuery, { id }).then(({ data, errors }) => {
      this.setState(prevState => ({
        priceHistory: {
          ...prevState.priceHistory,
          [id]: errors ? null : { prices: data.price.history.nodes },
        },
      }));
    });
  }

  priceHistoryGraph(startDate) {
    const {
      data: { product },
    } = this.props;
    const { t } = this.context;

    const productNodes = filterProductNodes(
      product.statistics.nodes,
      startDate
    );

    return {
      name: PRICE_HISTORY,
      legend: {
        label: t('product', 'price-history.actual-price'),
        color: '#4895B6',
        lineType: 'normal',
      },
      xScaleData: productNodes.map(({ date }) => new Date(date)),
      yScaleData: productNodes.map(({ lowestPrice }) => lowestPrice),
      render: props => (
        <PriceHistoryCurve key={props.name} data={productNodes} {...props} />
      ),
    };
  }

  pricePredictionGraph() {
    const {
      data: { product },
    } = this.props;
    const { t } = this.context;

    if (!product.priceForecast || product.priceForecast.length === 0) {
      return null;
    }

    return {
      name: PRICE_PREDICTION,
      legend: {
        label: t('product', 'price-history.pre-release'),
        color: '#5fbc40',
        lineType: 'dashed',
        description: t('product', 'price-history.pre-release-info'),
      },
      xScaleData: product.priceForecast.map(({ date }) => new Date(date)),
      yScaleData: product.priceForecast.map(({ price }) => price),
      render: props => (
        <PricePrediction
          key={props.name}
          data={product.priceForecast}
          {...props}
        />
      ),
    };
  }

  priceTrendGraph(startDate) {
    const {
      data: { product },
    } = this.props;
    const { t } = this.context;

    if (!product.priceTrend || product.priceTrend.length === 0) {
      return null;
    }

    const priceTrendData = interpolateMeanPrice(
      product.priceTrend.map(d => ({ ...d, date: new Date(d.date) })),
      new Date(startDate)
    );

    return {
      name: PRICE_TREND,
      legend: {
        label: t('product', 'price-history.price-trend'),
        color: '#c61e21',
        lineType: 'normal',
        description: t('product', 'price-history.price-trend-info'),
      },
      xScaleData: priceTrendData.map(({ date }) => new Date(date)),
      yScaleData: priceTrendData.map(({ highestPrice }) => highestPrice),
      render: props => (
        <PriceTrend key={props.name} data={priceTrendData} {...props} />
      ),
    };
  }

  medianPrice(startDate) {
    const {
      data: { product },
    } = this.props;
    const { t } = this.context;

    if (!product.medianPrice || product.medianPrice.length === 0) {
      return null;
    }

    const medianPriceData = filterProductNodes(
      product.medianPrice.map(d => ({ ...d, date: new Date(d.date) })),
      new Date(startDate)
    );

    return {
      name: MEDIAN_PRICE,
      legend: {
        label: t('product', 'price-history.median-price'),
        color: 'rgb(254, 123, 35)',
        lineType: 'normal',
        description: t('product', 'price-history.median-price-info'),
      },
      yScaleData: medianPriceData.map(({ hdi70 }) => hdi70.up),
      render: props => (
        <MedianPrice key={props.name} data={medianPriceData} {...props} />
      ),
    };
  }

  maxPrice(startDate) {
    const {
      data: { product },
    } = this.props;
    const { t } = this.context;

    if (!product.medianPrice || product.medianPrice.length === 0) {
      return null;
    }

    const medianPriceData = filterProductNodes(
      product.medianPrice.map(d => ({ ...d, date: new Date(d.date) })),
      new Date(startDate)
    );

    return {
      name: MAX_PRICE,
      legend: {
        label: t('product', 'price-history.max-price'),
        color: 'rgb(120, 63, 104)',
        lineType: 'normal',
        description: t('product', 'price-history.max-price-info'),
      },
      yScaleData: medianPriceData.map(({ medianPrice, maxPrice }) =>
        Math.min(medianPrice * 3, maxPrice)
      ),
      render: props => (
        <MaxPrice key={props.name} data={medianPriceData} {...props} />
      ),
    };
  }

  importantDatesGraph() {
    const {
      data: { importantDates },
    } = this.props;

    return {
      name: IMPORTANT_DATES,
      tooltipData: ({ date }) => ({
        importantDates: importantDates.filter(
          ({ startDate, endDate }) => startDate <= date && date <= endDate
        ),
      }),
      render: props => (
        <ImportantDates
          key={props.name}
          dates={importantDates.map(date => ({
            ...date,
            startDate: new Date(date.startDate),
            // Makes the graph run up to the end of the day, for a nicer appearance.
            endDate: new Date(`${date.endDate} 23:59:59.999`),
          }))}
          {...props}
        />
      ),
    };
  }

  render() {
    const {
      data: { product },
      fetchMoreData,
      hideModal,
      showModal,
    } = this.props;

    const {
      selectedPrices,
      expandedPrices,
      priceHistory,
      selectedStartDate,
      selectedGraphs,
    } = this.state;

    const { t } = this.context;

    const nodes = product.statistics && product.statistics.nodes;

    // In the unlikely case the product has no history at all (e.g. brand new
    // product), fall back to today. The price chart will be hidden anyway.
    // TODO: Better non-ideal state handling, e.g. showing a text like:
    //
    //   There is no price history for this product.
    const minDate = nodes && nodes.length > 0 ? nodes[0].date : Date.now();
    const maxDate =
      nodes && nodes.length > 0 ? nodes[nodes.length - 1].date : Date.now();

    const defaultStartDate = new Date(minDate);
    defaultStartDate.setFullYear(defaultStartDate.getFullYear() - 2);

    let productNodes = filterProductNodes(
      nodes,
      selectedStartDate.min || defaultStartDate
    );

    // Show the whole graph from the beginning for products without any newer
    // prices.
    if (!productNodes.some(x => x.lowestPrice > 0)) {
      defaultStartDate.setFullYear(1971);

      productNodes = filterProductNodes(
        nodes,
        selectedStartDate.min || defaultStartDate
      );
    }

    const isActive = graph =>
      !!graph && selectedGraphs.some(name => name === graph.name);

    const graphs = [
      this.importantDatesGraph(),
      this.medianPrice(selectedStartDate.min || defaultStartDate),
      this.maxPrice(selectedStartDate.min || defaultStartDate),
      this.priceTrendGraph(selectedStartDate.min || defaultStartDate),
      this.pricePredictionGraph(),
      this.priceHistoryGraph(selectedStartDate.min || defaultStartDate),
    ].filter(isActive);

    const xScaleData = graphs
      .filter(x => Boolean(x.xScaleData))
      .reduce((acc, x) => acc.concat(x.xScaleData), []);

    const yScaleData = graphs
      .filter(x => Boolean(x.yScaleData))
      .reduce((acc, x) => acc.concat(x.yScaleData), []);

    return (
      <>
        <Modal id="share-graph" onRequestClose={() => hideModal()} width={500}>
          <h2>{t('product', 'price-history.share-graph-heading')}</h2>
          <p>{t('product', 'price-history.share-graph-instructions')}</p>
          <S.ShareGraphLinkInput
            inputRef={this.shareGraphInputRef}
            defaultValue={this.getShareUrl()}
          />
          <S.CopyLinkButton
            onClick={() => {
              this.shareGraphInputRef.current.select();
              document.execCommand('copy');
            }}
          >
            {t('product', 'price-history.share-graph-copy-link')}
          </S.CopyLinkButton>
        </Modal>
        {product.statistics && product.statistics.pageInfo.lowestPrice && (
          <div
            className="graph"
            data-testid="graph"
            data-test="PriceHistoryGraph"
          >
            <S.PriceHistoryGraph
              withTitle={false}
              prices={product.history && product.history.nodes}
              priceHistory={priceHistory}
              selectedPrices={selectedPrices}
              withSummary
              pageInfo={product.statistics.pageInfo}
              nodes={productNodes}
              predictedPrices={
                product.priceForecast ? product.priceForecast : []
              }
              releaseDate={product.releaseDate}
              xScaleData={xScaleData}
              yScaleData={yScaleData}
              graphs={graphs}
              graphAspectRatio={2.5}
              legend={
                <>
                  <S.ShareGraphButton onClick={() => showModal('share-graph')}>
                    <ShareIcon />
                  </S.ShareGraphButton>
                  <GraphSelector
                    options={[
                      {
                        name: t('product', 'price-history.actual-price'),
                        value: PRICE_HISTORY,
                        active: selectedGraphs.indexOf(PRICE_HISTORY) >= 0,
                        disabled: false,
                      },
                      {
                        name: t('product', 'price-history.pre-release'),
                        value: PRICE_PREDICTION,
                        active: selectedGraphs.indexOf(PRICE_PREDICTION) >= 0,
                        disabled:
                          !product.priceForecast ||
                          product.priceForecast.length === 0,
                      },
                      {
                        name: t('product', 'price-history.price-trend'),
                        value: PRICE_TREND,
                        active: selectedGraphs.indexOf(PRICE_TREND) >= 0,
                        disabled:
                          !product.priceTrend ||
                          product.priceTrend.length === 0,
                      },
                      {
                        name: t('product', 'price-history.median-price'),
                        value: MEDIAN_PRICE,
                        active: selectedGraphs.indexOf(MEDIAN_PRICE) >= 0,
                        disabled:
                          !product.medianPrice ||
                          product.medianPrice.length === 0,
                      },
                      {
                        name: t('product', 'price-history.max-price'),
                        value: MAX_PRICE,
                        active: selectedGraphs.indexOf(MAX_PRICE) >= 0,
                        disabled:
                          !product.medianPrice ||
                          product.medianPrice.length === 0,
                      },
                      {
                        name: t('product', 'price-history.important-dates'),
                        value: IMPORTANT_DATES,
                        active: selectedGraphs.indexOf(IMPORTANT_DATES) >= 0,
                        disabled: false,
                      },
                    ]}
                    onAdd={value =>
                      this.setState(prevState => ({
                        selectedGraphs: prevState.selectedGraphs.concat(value),
                      }))
                    }
                    onRemove={value =>
                      this.setState(prevState => ({
                        selectedGraphs: prevState.selectedGraphs.filter(
                          name => name !== value
                        ),
                      }))
                    }
                  />
                  <IntervalSelector
                    min={minDate}
                    max={maxDate}
                    selected={selectedStartDate.period || 'all'}
                    onChange={option =>
                      this.setState({ selectedStartDate: option })
                    }
                  />
                </>
              }
            />

            {product.history && (
              <StorePriceLegend
                prices={product.history.nodes.filter(
                  price => selectedPrices[price.id]
                )}
                colors={selectedPrices}
              />
            )}
          </div>
        )}

        <AdContainer
          placement="panorama_extra"
          format="panorama_1_wde"
          min={861}
          max={10000}
          scalable
          scaleBase=".pj-ui-panel--row"
          spacing={{ top: '10px', bottom: '10px' }}
        />
        <AdContainer
          placement="dubbelpanorama1"
          format="panorama_1"
          min={0}
          max={860}
          spacing={{ top: '10px', bottom: '10px' }}
        />

        {product.history && (
          <S.FetchMorePrices
            pageInfo={product.history.pageInfo}
            onFetchMoreData={() => {
              fetchMoreData({
                priceHistoryTable: true,
                offset:
                  product.history.pageInfo.limit +
                  product.history.pageInfo.offset +
                  1,
              });
            }}
          >
            <PriceHistoryTable
              productId={product.id}
              prices={product.history.nodes}
              selectedPrices={selectedPrices}
              expandedPrices={expandedPrices}
              priceHistory={priceHistory}
              onToggleExpandPrice={id => {
                this.fetchPriceHistory(id);
                this.setState(prevState => ({
                  expandedPrices: {
                    ...prevState.expandedPrices,
                    [id]: !prevState.expandedPrices[id],
                  },
                }));
              }}
              onTogglePriceGraph={(id, color) => {
                this.fetchPriceHistory(id);
                this.setState(prevState => ({
                  selectedPrices: {
                    ...prevState.selectedPrices,
                    [id]: prevState.selectedPrices[id] ? undefined : color,
                  },
                }));
              }}
            />
          </S.FetchMorePrices>
        )}
      </>
    );
  }
}

const { func, shape } = PropTypes;

ProductPriceHistory.defaultProps = {
  data: {
    product: undefined,
  },
};

ProductPriceHistory.propTypes = {
  data: shape(graphqlPropTypes(ProductPriceHistoryPageQuery)),
  fetchMoreData: func.isRequired,
  hideModal: func.isRequired,
  showModal: func.isRequired,
};

ProductPriceHistory.contextType = AppContext;

const ProductPriceHistoryWithRedux = connect(null, {
  ...ModalActions,
  adsViewUpdateAction,
})(ProductPriceHistory);

export default withRouter(props => (
  <FetchContext.Consumer>
    {fetchContext => (
      <ProductPriceHistoryWithRedux {...props} fetchContext={fetchContext} />
    )}
  </FetchContext.Consumer>
));
